import { PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, message, Modal, ModalProps } from 'antd';
import FormField from 'components/FormField';
import { getStoreCount, Store, StoreScopesModal } from 'components/StoreScopes';
import { Formik } from 'formik';
import { StoreGroupWithScopes, storeScopes2storeIds } from './share';
import { createStoreGroup, updateStoreGroup } from '@knowledge/service/api/general';
import { useTranslate } from '@tolgee/react';
import {
  removeStoreScopesAll,
  StoreSelect,
  UnConfigStore,
} from 'views/BootGuide/views/AiModel/share';
import { useState } from 'react';

interface FV extends Util.PartialPartial<StoreGroupWithScopes, 'id'> {}

const defaultInitialValues: FV = {
  name: '',
  storeIds: [],
  storeScopes: [],
  isDefault: false,
};

function StoreGroupModal({
  storeGroupWithScopes,
  mode,
  stores,
  storeMap,
  onSave,
  onCancel,
  onSubmit,
  ...otherProps
}: ModalProps & {
  storeGroupWithScopes?: StoreGroupWithScopes;
  mode?: 'name' | 'store';
  stores: Store[];
  storeMap: Partial<Record<number, UnConfigStore>>;
  onSave?: () => Promise<any>;
  onSubmit?: (values: FV) => Promise<any>;
}) {
  const { t } = useTranslate();

  const [visible, setVisible] = useState(false);

  if (storeGroupWithScopes && mode === 'store') {
    const { storeScopes, ...storeGroup } = storeGroupWithScopes;
    return (
      <StoreScopesModal
        allowEmpty
        showFooterTips={false}
        value={storeScopes}
        storeDataSource={stores}
        storeMap={storeMap}
        onCancel={() => onCancel?.({} as any)}
        {...otherProps}
        onChange={(storeScopes) => {
          return updateStoreGroup({
            ...storeGroup,
            storeIds: storeScopes2storeIds(removeStoreScopesAll(storeScopes, stores)),
          })
            .then(() => onSave?.())
            .then(() => {
              onCancel?.({} as any);
              message.success(t('phrase.保存成功'));
            })
            .catch(() => {
              message.error(t('phrase.保存失败，请稍后再试'));
            });
        }}
      />
    );
  }

  return (
    <Formik
      enableReinitialize
      initialValues={storeGroupWithScopes || defaultInitialValues}
      onSubmit={(values) => {
        const { id, storeScopes, ...others } = values;
        const storeScopesRemovedAll = removeStoreScopesAll(storeScopes, stores);
        if (onSubmit) {
          return onSubmit({
            ...values,
            storeScopes: storeScopesRemovedAll,
          });
        }
        const storeIds = storeScopes2storeIds(storeScopesRemovedAll);
        return (
          id
            ? updateStoreGroup({
                id,
                ...others,
                storeIds,
              })
            : createStoreGroup({
                ...others,
                storeIds,
              })
        )
          .then(() => onSave?.())
          .then(() => {
            onCancel?.({} as any);
            message.success(t('phrase.保存成功'));
          })
          .catch((e) => {
            if (e?.response?.data?.code === 3006) {
              message.warn(t('与已有配置组名称一致'));
            } else {
              message.error(t('phrase.保存失败，请稍后再试'));
            }
          });
      }}
    >
      {({ isSubmitting, handleSubmit, resetForm }) => {
        return (
          <Modal
            title={
              !storeGroupWithScopes
                ? t('新增配置组')
                : mode === 'name'
                ? t('编辑配置组名称')
                : t('编辑配置组')
            }
            width={400}
            okText={t('保存')}
            okButtonProps={{
              loading: isSubmitting,
              onClick: () => handleSubmit(),
            }}
            onCancel={(e) => {
              onCancel?.(e);
              resetForm();
            }}
            {...otherProps}
          >
            <Form layout="vertical">
              <FormField<FV['name']>
                name="name"
                required
                label={t('配置组名称')}
                validate={(name) => (!name.trim() ? t('请输入配置组名称') : undefined)}
              >
                {({ field }) => {
                  return (
                    <Input
                      placeholder={t('请输入配置组名称')}
                      showCount
                      maxLength={50}
                      {...field}
                    />
                  );
                }}
              </FormField>
              {mode !== 'name' && (
                <FormField<FV['storeScopes']> name="storeScopes" label={t('marketing.店铺')}>
                  {({ field: { value, onChange } }) => {
                    return (
                      <div>
                        {!getStoreCount(value) ? (
                          <Button
                            style={{ paddingLeft: 0 }}
                            type="link"
                            icon={<PlusOutlined />}
                            onClick={() => setVisible(true)}
                          >
                            {t('添加店铺')}
                          </Button>
                        ) : (
                          <StoreSelect
                            value={value}
                            storeMap={storeMap}
                            onEdit={() => setVisible(true)}
                          />
                        )}
                        <StoreScopesModal
                          allowEmpty
                          showFooterTips={false}
                          visible={visible}
                          onCancel={() => setVisible(false)}
                          value={value}
                          onChange={onChange}
                          storeDataSource={stores}
                          storeMap={storeMap}
                        />
                      </div>
                    );
                  }}
                </FormField>
              )}
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
}

export default StoreGroupModal;
